import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Button, Group, Stack } from '@mantine/core';
import { IconCancel as CancelIcon, IconDeviceFloppy as SaveIcon } from '@tabler/icons-react';
import { ReactNode } from 'react';
import { DefaultValues, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ZodSchema } from 'zod';

import { BaseCriteriaItemSchema } from '@/module/ResearchPerfEval/model/ResearchPerfEvalSchema';

import { Content, TextInputControl } from '@/core/components';
import { FileInputControl } from '@/core/components/Layout/Form/components/FileInputControl';

interface ResearchPerfEvalCriteriaItemFormProps<T extends BaseCriteriaItemSchema> {
  onChange: (request: T) => void;
  onCancel: () => void;

  defaultValues: DefaultValues<T>;

  isSubmitting: boolean;

  schema: ZodSchema<T>;

  renderExtraFields: (isSubmitting: boolean) => ReactNode;
}

export function BaseResearchPerfEvalCriteriaItemForm<T extends BaseCriteriaItemSchema>(
  props: ResearchPerfEvalCriteriaItemFormProps<T>,
) {
  const { onChange, defaultValues, isSubmitting, onCancel, schema, renderExtraFields } = props;
  const { t } = useTranslation();

  const form = useForm<T>({
    defaultValues,
    resolver: zodResolver(schema),
  });

  return (
    <Content type="full">
      <FormProvider {...form}>
        <Box
          flex={1}
          mb="10px"
          p="sm"
          component="form"
          onSubmit={form.handleSubmit(onChange)}
        >
          <Stack>
            {renderExtraFields(isSubmitting)}
            <Group>
              <TextInputControl
                name="link"
                label={t('researchPerfEval.form.link')}
                isSubmitting={isSubmitting}
              />
              <TextInputControl
                name="note"
                label={t('researchPerfEval.form.note')}
                isSubmitting={isSubmitting}
              />
            </Group>
            <FileInputControl
              name="newFile"
              label={t('researchPerfEval.form.file')}
              placeholder={defaultValues?.file?.title ?? t('researchPerfEval.form.file')}
              description={t('researchPerfEval.form.fileDescription')}
              isSubmitting={isSubmitting}
              accept="image/png,image/jpeg,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/zip,application/vnd.rar,application/x-rar-compressed,application/x-gzip,application/x-7z-compressed"
            />
          </Stack>
          <Group
            mt="lg"
            justify="flex-end"
          >
            {onCancel && (
              <Button
                size="sm"
                leftSection={<CancelIcon />}
                variant="outline"
                onClick={onCancel}
                disabled={isSubmitting}
              >
                {t('button.cancel')}
              </Button>
            )}
            <Button
              size="sm"
              leftSection={<SaveIcon />}
              type="submit"
              disabled={isSubmitting}
              loading={isSubmitting}
            >
              {t('button.save')}
            </Button>
          </Group>
        </Box>
      </FormProvider>
    </Content>
  );
}
